import React, { useCallback, useEffect, useState } from 'react';
import { Button, Table, TextBox } from '@ds/ui';
import { apiFetch, handleApiError, handleApiErrorWithBanner } from '../../utils/apiClient';
import BannerInfo from '../../types/BannerInfo';
import ExportInfo from '../../types/ExportInfo';
import { ImportInfo } from '../../types/ImportInfo';

interface HSMKey {
  label: string;
  checksum: string;
  startDate: string;
  usedBy: string | null;
  keyType: number;
  keyObject: number;
  publicKey: string | null;
}

interface NamedKeysInfoData {
  driver: string | null;	
  description: string | null;	
  manufacturer: string | null;	
  version: string | null;	
}

interface NamedKeyPackageResponse {
  namedKeyPackage: {
    hsmKeys: HSMKey[];
    cumulativeChecksum: string;
    namedKeyChecksums: { [key: string]: string };
    error: string | null;
  }
}

interface NamedKeyInfoProps {
  onBannerUpdate: (bannerInfo: BannerInfo | null) => void,
  onNamedKeyConfiguration: (namedKeyConfiguration: boolean) => void,
  onExportInfoUpdate?: (exportInfo: ExportInfo) => void,
}

export const NamedKeys: React.FC<NamedKeyInfoProps> = ({ onBannerUpdate, onNamedKeyConfiguration, onExportInfoUpdate }) => {
  const [keyPackage, setKeyPackage] = useState<NamedKeyPackageResponse['namedKeyPackage'] | null>(null);
  const [createKeyLabel, setCreateKeyLabel] = useState<string>('');
  const [showAllKeys, setShowAllKeys] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState<string>('');
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [originalValue, setOriginalValue] = useState("New Key Name");
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [namedKeysInfo, setNamedKeysInfo] = useState<NamedKeysInfoData | null>(null);

  const formatChecksum = (checksum: string | null): string => {
    if (!checksum) return '';
    if (checksum.length > 19) {
      return `${checksum.substring(0, 8)}...${checksum.substring(checksum.length - 8)}`;
    }
    return checksum;
  };

  const toggleShowAllKeys = () => setShowAllKeys(!showAllKeys);

  const handleFocus = () => {
    setIsEditing(true);
    setOriginalValue(inputValue);
  };

  const handleBlur = () => {
    if (inputValue === originalValue) {
      setIsEditing(false);
    }
  };
  
  const fetchNamedKeysInfo = useCallback(async () => {
    try {
      const response = await apiFetch('Admin/Home/GetNamedKeysInfo', {
        method: 'GET'
      });
      
      if (!response.ok) {
        await handleApiError(response);
      }
      const data = await response.json();
      const namedKeysInfo = data.namedKeyInfo.result;
      const isConfigured = !!namedKeysInfo?.driver;
      setNamedKeysInfo(namedKeysInfo);
      onNamedKeyConfiguration(isConfigured);
    } catch (err) {
      console.error('Error fetching named keys data:', err);
      onNamedKeyConfiguration(false);
    }
  }, []);

  const fetchNamedKeys = async () => {
    try {
      const response = await apiFetch('Admin/Home/GetNamedKeyPackage');
      if (!response.ok) {
        await handleApiError(response);
      }
      const data: NamedKeyPackageResponse = await response.json();
      setKeyPackage(data.namedKeyPackage);
    } catch (err) {
      setError('Error fetching HSM key data');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateKey = async () => {
    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append('deriver', 'NamedKey');
      formData.append('keylabel', createKeyLabel);
      
      const response = await apiFetch('Admin/Home/HSMKeyAdd', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        await handleApiError(response);
      }
      
      // Add a small delay before fetching updated data, otherwise the new key will not be shown
      setTimeout(() => {
        fetchNamedKeys();
      }, 1000); // 1 second delay
      
      // Clear the input field after successful creation
      setCreateKeyLabel('');
      onBannerUpdate({ type: 'success', message: 'Named key created successfully' });
      setIsSuccess(true);
      setTimeout(() => setIsSuccess(false), 5000); // Reset after 5 seconds
    } catch (err) {
      console.error(err);
      onBannerUpdate({ type: 'danger', message: 'Error creating named key: ' + err });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchNamedKeysInfo();
    fetchNamedKeys();
  }, []);

  const handleExport = useCallback(async (selectedKeyLabel: string) => {
    try {
      const formData = new FormData()
      formData.append('keyLabel', selectedKeyLabel)
      
      const response = await apiFetch('Admin/Home/ExportSingleNamedKey', {
        method: 'POST',
        body: formData
      })
      
      if (!response.ok) { 
        const errorMessage = await handleApiErrorWithBanner(response);
        onBannerUpdate({ 
          type: 'danger',
          message: `Failed to export key: ${errorMessage}`,
          retryAction: () => handleExport(selectedKeyLabel) 
        });
        throw new Error(`Failed to export key: ${errorMessage}`);
      }
      
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Fetch the export info to start the workflow
      const exportResponse = await apiFetch('Admin/Home/GetExportInfo');
      if (!exportResponse.ok) {
        await handleApiError(exportResponse);
      }
      const data = await exportResponse.json();
      const exportInfo = data.exportInfo;
      
      // Notify parent component of export info update
      onExportInfoUpdate?.(exportInfo);

      onBannerUpdate({ type: 'success', message: 'Export started successfully' });
    } catch (error) {
      console.error('Error exporting key:', error)
      onBannerUpdate({ 
        type: 'danger', 
        message: 'Failed to export keys. Please try again.', 
        retryAction: () => handleExport(selectedKeyLabel)
      })
      throw new Error('Failed to export keys');
    } 
  }, [onBannerUpdate, onNamedKeyConfiguration, onExportInfoUpdate]);

  return (
    <div>
      <Table columnSettings={[
        { alignment: 'left', width: '10rem' },
        { alignment: 'left', width: '1.25rem' },
        { alignment: 'left', width: '3.125rem' },
        { alignment: 'left', width: '6.25rem' }
      ]}>
        <Table.Body>
          <Table.Row>
            <Table.Cell _style={{ whiteSpace: 'normal', verticalAlign: 'top' , paddingTop: 0, paddingLeft: 16}}>
              <span>{keyPackage?.hsmKeys.length}</span>
            </Table.Cell>
            <Table.Cell _style={{ whiteSpace: 'normal', verticalAlign: 'top', paddingTop: 0 }} colSpan={4}>(Checksum: {keyPackage?.cumulativeChecksum})</Table.Cell>
          </Table.Row>
          <Table.Row>     
            <Table.Cell _style={{ whiteSpace: 'normal', verticalAlign: 'top' }} colSpan={4}>
              <div>
                <TextBox
                  width="17.5rem"
                  label="Create New Key"
                  type="text"
                  placeholder="New Key Name"
                  value={createKeyLabel}
                  onChange={(e) => setCreateKeyLabel(e.target.value)}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  endElement={<Button 
                    onClick={handleCreateKey} 
                    disabled={isLoading}
                    accessibilityText="Create New Key" 
                    icon={isLoading ? "processSpinner" : (isSuccess ? "check" : (isEditing ? "arrowRight" : "pencil"))}
                    kind={isSuccess ? "primary" : "tertiary"}
                    size="small"/>}
                />
              </div>
            </Table.Cell>
          </Table.Row>

          <Table.Row>
            <Table.Cell _style={{ whiteSpace: 'nowrap', verticalAlign: 'top' }} colSpan={2}>
              <Button 
                kind="secondary" 
                text={showAllKeys ? "Hide all keys" : "Show all keys"} 
                onClick={toggleShowAllKeys}
              />
            </Table.Cell>
          </Table.Row>
        </Table.Body>
      </Table>

      {showAllKeys && (
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell text="Label" />
              <Table.HeaderCell text="Date Created" />
              <Table.HeaderCell text="Checksum" />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {keyPackage?.hsmKeys.map((key, index) => (
              <Table.Row key={index}>
                <Table.Cell>{key.label}</Table.Cell>
                <Table.Cell>{key.startDate}</Table.Cell>
                <Table.Cell>
                  <span title={key.checksum || ''}>{formatChecksum(key.checksum)}</span>
                </Table.Cell>
                <Table.Cell _style={{ whiteSpace: 'normal', verticalAlign: 'top', paddingTop: 0 }} colSpan={4}>
                  <Button 
                    text="Export" 
                    kind="secondary" 
                    onClick={() => handleExport(key.label)}
                  />
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </div>
  );
};

export default NamedKeys;

